import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
    ChevronLeft,
    ChevronRight,
    ChevronDown,
} from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { useSettings } from "@/lib/settings-context";
// iter-119w: NAV is now in `lib/nav-spec.js` so Settings → RBAC tab can
// derive its categories from the SAME source (single source of truth).
// Add/remove an entry there and BOTH the sidebar and the RBAC matrix
// update — no parallel updates needed.
import { NAV } from "@/lib/nav-spec";
import { useEntitlements } from "@/lib/use-entitlements";

// Nav modules whose visibility is ALSO governed by a plan feature. iter-2026:
// instead of HIDING (which left OAs unaware the feature exists), a
// plan-disabled but RBAC-allowed module now SHOWS with an "Upgrade" lock.
const MODULE_FEATURE = { my_apps: "app_catalog", marketplace: "app_catalog", branding_page: "custom_branding", app_studio: "app_studio" };
import SidebarUsageBadge from "./SidebarUsageBadge";
import { Lock } from "lucide-react";

const STORAGE_KEY = "analyzio_sidebar_open_groups_v2";

function loadOpenGroups() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch { return null; }
}
function saveOpenGroups(state) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch {}
}

export default function Sidebar({ collapsed, onToggle, mobileMode = false, onLinkClick }) {
    const { user } = useAuth();
    const { canAccess, settings } = useSettings();
    const { entitled, openUpgrade, role } = useEntitlements();
    // Plan → RBAC. Visibility is driven by RBAC (`canAccess`). Plan entitlement
    // no longer HIDES a module — when RBAC allows it but the plan doesn't, the
    // item stays visible but LOCKED (Upgrade badge). RBAC semantics unchanged.
    const canUseModule = (module) => canAccess(module);
    const moduleLocked = (module) => {
        const feat = MODULE_FEATURE[module];
        return !!feat && role !== "super_admin" && !entitled(feat);
    };
    const nav = useNavigate();
    const location = useLocation();

    // iter-110.9: support live preview from Settings → Branding. The form
    // dispatches a `branding:preview` event on every change; we shadow the
    // persisted branding with whatever the form currently has so the SA
    // sees app_name / logo / favicon updates instantly (CSS vars handle
    // colors / typography on their own). We DO NOT persist the preview —
    // when the form unmounts, the persisted settings re-take over.
    const persisted = settings?.branding || {};
    const [livePreview, setLivePreview] = useState(null);
    useEffect(() => {
        const onPreview = (e) => setLivePreview(e.detail || null);
        window.addEventListener("branding:preview", onPreview);
        return () => window.removeEventListener("branding:preview", onPreview);
    }, []);
    const branding = livePreview || persisted;
    // When the form has been touched (livePreview set), respect blank
    // app_name verbatim — even an empty string. Persisted state still
    // falls back to "Analyzio".
    const rawAppName = branding.app_name;
    const appNameBlank = livePreview ? !(rawAppName && String(rawAppName).trim())
                                     : !persisted.app_name?.trim();
    const appName = appNameBlank ? "Analyzio" : String(rawAppName).trim();
    const logoUrl = branding.logo_url;
    const faviconUrl = branding.favicon_url;

    // iter-108: when SA leaves app_name empty, show the logo full-width
    // (no text label). When the sidebar is collapsed, prefer the favicon
    // so brand identity stays compact yet recognizable.
    // The sidebar bg always reads from --brand-sidebar-bg (with --card
    // fallback) so the live-preview on Settings → Branding works without
    // a save. The icon color is only forced inline when the SA has picked
    // one — otherwise we leave Tailwind's text-* classes alone so active
    // and hover states keep their color variation.
    const sidebarStyle = { backgroundColor: "var(--brand-sidebar-bg, hsl(var(--card)))" };
    const iconStyle = { color: "var(--brand-sidebar-icon, currentColor)" };

    // For groups: visible iff (a) group module itself is accessible AND
    // (b) ≥1 child is accessible. The group module key (e.g. `insights_group`)
    // is itself an RBAC entry — SA can flip it to '-' to hide the entire
    // submenu regardless of child permissions.
    const visible = NAV.filter((n) => {
        if (n.children) {
            if (!canAccess(n.key)) return false;
            return n.children.some((c) => canUseModule(c.module));
        }
        return canUseModule(n.module);
    });

    // Track which group parents are expanded. Persisted to localStorage.
    // Default for ALL roles/users: every group COLLAPSED. Groups open only
    // on explicit user click (never auto-expanded by route or on login).
    const [openGroups, setOpenGroups] = useState(() => loadOpenGroups() || {});

    const toggleGroup = (key) => setOpenGroups((prev) => {
        const next = { ...prev, [key]: !prev[key] };
        saveOpenGroups(next);
        return next;
    });

    return (
        <aside
            className={`${mobileMode ? "w-full" : (collapsed ? "w-[64px]" : "w-[240px]")} shrink-0 ${mobileMode ? "" : "border-r border-border h-screen sticky top-0"} bg-card flex flex-col ${mobileMode ? "h-full" : "transition-[width] duration-200"}`}
            style={sidebarStyle}
            data-testid="app-sidebar"
        >
            <div
                className={`h-[60px] flex items-center ${collapsed && !mobileMode ? "justify-center" : "px-5"} border-b border-border cursor-pointer`}
                onClick={() => { nav("/"); onLinkClick && onLinkClick(); }}
                data-testid="sidebar-brand"
            >
                {/* iter-108: branding header — three layouts:
                    1) Collapsed → favicon (or logo) only, square 28px
                    2) Expanded + blank app_name + logo → full-width logo
                    3) Expanded normal → small logo + app_name text */}
                {collapsed ? (
                    faviconUrl || logoUrl ? (
                        <img src={faviconUrl || logoUrl} alt={appName} className="w-7 h-7 object-contain" data-testid="sidebar-collapsed-icon" />
                    ) : (
                        <div className="w-7 h-7 bg-primary flex items-center justify-center">
                            <span className="text-primary-foreground text-sm font-semibold">{appName[0]}</span>
                        </div>
                    )
                ) : appNameBlank && logoUrl ? (
                    /* iter-108 fix: `w-full` forces the image to span the
                       sidebar's content width (200px); `object-contain`
                       preserves aspect ratio; `max-h-12` (48px) caps stacked
                       icon+wordmark logos so they don't break the 60px
                       header. Previously `max-h-9 max-w-full` left square
                       logos rendering at 36×36 — the SA-reported regression. */
                    <img src={logoUrl} alt={appName} className="w-full h-auto max-h-12 object-contain" data-testid="sidebar-fullwidth-logo" />
                ) : (
                    <div className="flex items-center gap-2.5 min-w-0">
                        {logoUrl ? (
                            <img src={logoUrl} alt={appName} className="w-7 h-7 object-contain shrink-0" />
                        ) : (
                            <div className="w-7 h-7 bg-primary flex items-center justify-center shrink-0">
                                <span className="text-primary-foreground text-sm font-semibold">{appName[0]}</span>
                            </div>
                        )}
                        <span className="text-[15px] font-semibold tracking-tight truncate">{appName}</span>
                    </div>
                )}
            </div>

            <nav className="flex-1 overflow-y-auto py-3 scrollbar-thin">
                {visible.map((n, i) => {
                    const Icon = n.icon;
                    const isGroup = !!n.children;

                    // Section caption — shown only when this visible item begins
                    // a new section. Because `visible` is already RBAC-filtered,
                    // a section whose every item is hidden simply never produces
                    // a caption (empty-section suppression is automatic).
                    const prevSection = i > 0 ? visible[i - 1].section : undefined;
                    const showCaption = n.section && n.section !== prevSection;
                    const caption = showCaption ? (
                        collapsed ? (
                            <div className="mx-3 my-2 border-t border-border" aria-hidden />
                        ) : (
                            <div className="px-5 pt-4 pb-1.5 flex items-center gap-2" data-testid={`sidebar-section-${n.section.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                                <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/70">{n.section}</span>
                            </div>
                        )
                    ) : null;

                    let itemEl;
                    if (isGroup) {
                        const visibleChildren = n.children.filter((c) => canUseModule(c.module));
                        const isOpen = !collapsed && !!openGroups[n.key];
                        const childActive = visibleChildren.some((c) => location.pathname === c.to || location.pathname.startsWith(c.to + "/"));
                        itemEl = (
                            <div>
                                <button
                                    type="button"
                                    onClick={() => collapsed ? null : toggleGroup(n.key)}
                                    data-testid={`sidebar-nav-${n.key}`}
                                    title={n.label}
                                    className={`group w-full flex items-center ${collapsed ? "justify-center px-0" : "px-5"} h-10 text-sm relative transition-colors ` +
                                        (childActive
                                            ? "text-foreground bg-secondary/40"
                                            : "text-muted-foreground hover:text-foreground hover:bg-secondary/40")}
                                >
                                    <Icon className="h-[17px] w-[17px] shrink-0" strokeWidth={1.75} style={iconStyle} />
                                    {!collapsed && (
                                        <>
                                            <span className="ml-3 flex-1 text-left">{n.label}</span>
                                            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isOpen ? "" : "-rotate-90"}`} />
                                        </>
                                    )}
                                </button>
                                {!collapsed && isOpen && visibleChildren.map((c) => {
                                    const CIcon = c.icon;
                                    if (moduleLocked(c.module)) {
                                        return (
                                            <button
                                                key={c.key}
                                                type="button"
                                                onClick={() => openUpgrade()}
                                                data-testid={`sidebar-nav-${c.key}`}
                                                title={`${c.label} — needs a plan upgrade`}
                                                className="w-full flex items-center pl-12 pr-4 h-9 text-[13px] text-muted-foreground/70 hover:text-foreground hover:bg-secondary/30 transition-colors"
                                            >
                                                <CIcon className="h-[14px] w-[14px] shrink-0 opacity-70" strokeWidth={1.75} style={iconStyle} />
                                                <span className="ml-2.5 flex-1 text-left">{c.label}</span>
                                                <Lock className="h-3 w-3 shrink-0 text-primary/70" />
                                            </button>
                                        );
                                    }
                                    return (
                                        <NavLink
                                            key={c.key}
                                            to={c.to}
                                            data-testid={`sidebar-nav-${c.key}`}
                                            onClick={() => onLinkClick && onLinkClick()}
                                            className={({ isActive }) =>
                                                `flex items-center pl-12 pr-5 h-9 text-[13px] relative transition-colors ` +
                                                (isActive
                                                    ? "text-foreground bg-secondary/50"
                                                    : "text-muted-foreground hover:text-foreground hover:bg-secondary/30")
                                            }
                                            title={c.label}
                                        >
                                            {({ isActive }) => (
                                                <>
                                                    {isActive && (
                                                        <span className="absolute left-0 top-0 bottom-0 w-[2px] bg-primary" />
                                                    )}
                                                    <CIcon className="h-[14px] w-[14px] shrink-0 opacity-70" strokeWidth={1.75} style={iconStyle} />
                                                    <span className="ml-2.5">{c.label}</span>
                                                </>
                                            )}
                                        </NavLink>
                                    );
                                })}
                                {collapsed && visibleChildren.map((c) => {
                                    const CIcon = c.icon;
                                    if (moduleLocked(c.module)) {
                                        return (
                                            <button key={c.key} type="button" onClick={() => openUpgrade()}
                                                    data-testid={`sidebar-nav-${c.key}`} title={`${c.label} — needs a plan upgrade`}
                                                    className="w-full flex items-center justify-center h-9 text-sm text-muted-foreground/70 hover:text-foreground hover:bg-secondary/30">
                                                <CIcon className="h-[14px] w-[14px] opacity-70" strokeWidth={1.75} style={iconStyle} />
                                            </button>
                                        );
                                    }
                                    return (
                                        <NavLink key={c.key} to={c.to} data-testid={`sidebar-nav-${c.key}`}
                                                 onClick={() => onLinkClick && onLinkClick()}
                                                 className={({ isActive }) =>
                                                     `flex items-center justify-center h-9 text-sm relative ` +
                                                     (isActive ? "text-foreground bg-secondary/50" : "text-muted-foreground hover:text-foreground hover:bg-secondary/30")
                                                 }
                                                 title={c.label}>
                                            <CIcon className="h-[14px] w-[14px] opacity-70" strokeWidth={1.75} style={iconStyle} />
                                        </NavLink>
                                    );
                                })}
                            </div>
                        );
                    } else {
                        itemEl = moduleLocked(n.module) ? (
                            <button
                                type="button"
                                onClick={() => openUpgrade()}
                                data-testid={`sidebar-nav-${n.key}`}
                                title={`${n.label} — needs a plan upgrade`}
                                className={`group w-full flex items-center ${collapsed ? "justify-center px-0" : "px-5"} h-10 text-sm relative transition-colors text-muted-foreground/70 hover:text-foreground hover:bg-secondary/40`}
                            >
                                <Icon className="h-[17px] w-[17px] shrink-0" strokeWidth={1.75} style={iconStyle} />
                                {!collapsed && <span className="ml-3 flex-1 text-left">{n.label}</span>}
                                {!collapsed && <Lock className="h-3 w-3 shrink-0 text-primary/70" />}
                            </button>
                        ) : (
                            <NavLink
                                to={n.to}
                                end={n.to === "/"}
                                data-testid={`sidebar-nav-${n.key}`}
                                onClick={() => onLinkClick && onLinkClick()}
                                className={({ isActive }) =>
                                    `group flex items-center ${collapsed ? "justify-center px-0" : "px-5"} h-10 text-sm relative transition-colors ` +
                                    (isActive
                                        ? "text-foreground bg-secondary/60"
                                        : "text-muted-foreground hover:text-foreground hover:bg-secondary/40")
                                }
                                title={n.label}
                            >
                                {({ isActive }) => (
                                    <>
                                        {isActive && (
                                            <span className="absolute left-0 top-0 bottom-0 w-[2px] bg-primary" />
                                        )}
                                        <Icon className="h-[17px] w-[17px] shrink-0" strokeWidth={1.75} style={iconStyle} />
                                        {!collapsed && <span className="ml-3">{n.label}</span>}
                                    </>
                                )}
                            </NavLink>
                        );
                    }

                    return (
                        <React.Fragment key={n.key}>
                            {caption}
                            {itemEl}
                        </React.Fragment>
                    );
                })}
            </nav>

            {/* iter-120 B4 — utilisation badge polling /billing/usage-summary */}
            <SidebarUsageBadge collapsed={collapsed} />

            {/* Collapse toggle is desktop-only — mobile drawer always shows
                full nav and dismisses via the Sheet's own overlay/close. */}
            {!mobileMode && onToggle && (
                <button
                    onClick={onToggle}
                    className="h-10 border-t border-border flex items-center justify-center text-muted-foreground hover:text-foreground text-xs"
                    data-testid="sidebar-collapse-toggle"
                >
                    {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
                    {!collapsed && <span className="ml-2">Collapse</span>}
                </button>
            )}
        </aside>
    );
}
